import { action, computed, observable, runInAction } from "mobx";
import { ViewModelBase, FieldType, isEmptyOrWhitespace, ApiResult, isNullOrUndefined } from "@shoothill/core";
import {
    AccidentFormModel,
    CasualtyUserDetailsDTO,
    IncidentFormAccidentAndRelatedResponseDTO,
    IncidentFormAccidentDTO,
    IncidentFormAccidentRelatedResponseDTO,
    UpsertIncidentFormAccidentAndRelatedRequestDTO,
} from "./AccidentFormModel";
import { IncidentFormWitnessStatementProjectDetailsDTO, IncidentUserDTO, GenericListItemDTO } from "../WitnessStatement/IncidentFormWitnessStatementModel";
import { AppUrls } from "../../../../../../AppUrls";
import { ServerViewModel } from "../../../../../../Globals/ViewModels/ServerViewModel";
import { IncidentDTO } from "../IncidentModel";
import moment from "moment/moment";
import { GenericFreeTextListItemDTO, GenericFreeTextListItemModel } from "../Supporting/GenericFreeTextListItemModel";
import { AccidentDocumentType, AccidentPhotoListItemDTO, AccidentPhotoListItemModel } from "./Supporting/AccidentPhotoListItemModel";
import { AccidentInjuryAreaItemDTO, AccidentInjuryAreaItemModel } from "./Supporting/AccidentInjuryAreaItemModel";
import { AccidentInjuryTypeItemDTO, AccidentInjuryTypeItemModel } from "./Supporting/AccidentInjuryTypeItemModel";
import { AccidentWitnessItemDTO, AccidentWitnessItemModel } from "./Supporting/AccidentWitnessItemModel";

export class AccidentFormViewModel extends ViewModelBase<AccidentFormModel> {
    constructor(id: string | null, incidentId: string | null) {
        super(new AccidentFormModel());
        this.setDecorators(AccidentFormModel);
        this.model.id = id;
        this.model.incidentId = incidentId!;

        isEmptyOrWhitespace(this.model.id) ? this.loadRelated() : this.loadWithRelated();

        (window as any).AccidentFormViewModel = this;
    }

    public server: ServerViewModel = new ServerViewModel();
    @observable
    public hasLoaded: boolean = false;
    @observable
    public incident: IncidentDTO | null = null;
    @observable
    public incidentUsers = observable<IncidentUserDTO>([]);
    public witnessUsers = observable<IncidentUserDTO>([]);
    public injuryAreas = observable<GenericListItemDTO>([]);
    public injuryTypes = observable<GenericListItemDTO>([]);
    public statusOfCasualtyTypes = observable<GenericListItemDTO>([]);
    public accidentCauses = observable<GenericListItemDTO>([]);
    public hazardSources = observable<GenericListItemDTO>([]);
    public medicalPracticeTypes = observable<GenericListItemDTO>([]);
    public drugTestResultTypes = observable<GenericListItemDTO>([]);
    @observable
    public casualtyUser: { id: string; displayName: string } | null = null;
    @observable
    public witnessUser: { id: string; displayName: string } | null = null;
    @observable
    public injuryArea: { id: string; displayName: string } | null = null;
    @observable
    public injuryType: { id: string; displayName: string } | null = null;
    @observable
    public causeOfAccident: { id: string; displayName: string } | null = null;
    @observable
    public sourceOfHazard: { id: string; displayName: string } | null = null;
    @observable
    public medicalPractice: { id: string; displayName: string } | null = null;
    @observable
    public drugTestResult: { id: string; displayName: string } | null = null;
    @observable
    public statusOfCasualty: { id: string; displayName: string } | null = null;
    @observable
    public incidentProjectDetails: IncidentFormWitnessStatementProjectDetailsDTO | null = null;

    @observable
    public casualtyUserDetails: CasualtyUserDetailsDTO | null = null;

    @observable
    public rootCausesValidationMessage: string | null = null;

    @action
    private setRootCausesValidationMessage = (val: string | null) => {
        this.rootCausesValidationMessage = val;
    };

    @observable
    public injuryTypesValidationMessage: string | null = null;

    @action
    private setInjuryTypesValidationMessage = (val: string | null) => {
        this.injuryTypesValidationMessage = val;
    };

    @observable
    public injuryAreasValidationMessage: string | null = null;

    @action
    private setInjuryAreasValidationMessage = (val: string | null) => {
        this.injuryAreasValidationMessage = val;
    };

    @observable
    public witnessesValidationMessage: string | null = null;

    @action
    private setWitnessesValidationMessage = (val: string | null) => {
        this.witnessesValidationMessage = val;
    };

    @action
    private resetListValidationMessages = () => {
        this.setRootCausesValidationMessage(null);
        this.setInjuryTypesValidationMessage(null);
        this.setInjuryAreasValidationMessage(null);
        this.setWitnessesValidationMessage(null);
    };

    @computed
    public get getInjuryAreaOptions(): GenericListItemDTO[] {
        return this.injuryAreas.filter((item) => this.model.incidentFormAccidentInjuryAreas.filter((i) => !i.isDeleted).findIndex((i) => i.injuryAreaId === item.id) === -1);
    }

    @computed
    public get getInjuryTypeOptions(): GenericListItemDTO[] {
        return this.injuryTypes.filter((item) => this.model.incidentFormAccidentInjuryTypes.filter((i) => !i.isDeleted).findIndex((i) => i.injuryTypeId === item.id) === -1);
    }

    @computed
    public get getWitnessesOptions(): GenericListItemDTO[] {
        return this.incidentUsers.filter((item) => this.model.incidentFormAccidentWitnesses.filter((i) => !i.isDeleted).findIndex((i) => i.witnessUserId === item.id) === -1);
    }

    @action
    public handleSetCasualtyUser = async (item: { id: string; displayName: string }) => {
        this.casualtyUser = item;
        this.model.casualtyUserId = item.id;

        if (item.id !== this.model.notInSystemOption.id) {
            this.model.casualtyNotInSystem = null;
            this.model.statusOfCasualtyId = null;
            this.model.casualtyPhoneNumber = null;
            this.model.casualtyEmailAddress = null;
            await this.getCasualtyUserDetails(item.id);
        } else {
            this.casualtyUserDetails = null;
        }
    };

    @action
    public handleSetStatusOfCasualty = (item: { id: string; displayName: string }) => {
        this.statusOfCasualty = item;
        this.model.statusOfCasualtyId = item.id;
    };

    @action
    public handleSetInjuryArea = (item: { id: string; displayName: string }) => {
        this.injuryArea = item;
    };

    @action
    public handleSetInjuryType = (item: { id: string; displayName: string }) => {
        this.injuryType = item;
    };

    @action
    public handleSetWitness = (item: { id: string; displayName: string }) => {
        this.witnessUser = item;
    };

    @action
    public handleSetCauseOfAccidentType = (item: { id: string; displayName: string }) => {
        this.causeOfAccident = item;
        this.model.causeOfAccidentTypeId = item.id;

        if (item.displayName !== "Other") {
            this.model.otherCauseOfAccident = null;
        }

        if (item.displayName !== "Fall from height") {
            this.model.fallHeight = null;
        }
    };

    @computed
    public get isOtherCauseOfAccident(): boolean {
        return this.causeOfAccident && this.causeOfAccident.displayName === "Other" ? true : false;
    }

    @computed
    public get isFallHeightCauseOfAccident(): boolean {
        return this.causeOfAccident && this.causeOfAccident.displayName === "Fall from height" ? true : false;
    }

    @computed
    public get isOtherSourceOfHazard(): boolean {
        return this.sourceOfHazard && this.sourceOfHazard.displayName === "Other" ? true : false;
    }

    @action
    public handleSetSourceOfHazardType = (item: { id: string; displayName: string }) => {
        this.sourceOfHazard = item;
        this.model.sourceOfHazardTypeId = item.id;

        if (item.displayName !== "Other") {
            this.model.otherSourceOfHazard = null;
        }
    };

    @action
    public handleSetMedicalPracticeType = (item: { id: string; displayName: string }) => {
        this.medicalPractice = item;
        this.model.whichMedicalPracticeId = item.id;
    };

    @action
    public handleSetDrugTestResultType = (item: { id: string; displayName: string }) => {
        this.drugTestResult = item;
        this.model.alcoholOrDrugTestingRequiredResultId = item.id;
    };

    @observable
    public isViewOnly: boolean = false;

    @computed
    public get getIsViewOnly(): boolean {
        return this.isViewOnly;
    }

    @action
    public setIsViewOnly = (val: boolean) => {
        this.isViewOnly = val;
    };

    @computed
    public get isFirstFormDisabled(): boolean {
        return this.isFormDisabled || (this.model.id !== null && this.model.id !== undefined && this.model.id !== "") || this.IsLoading || this.model.hasId;
    }

    @computed
    public get isSecondFormDisabled(): boolean {
        return this.isFormDisabled || this.IsLoading || !this.model.hasId || this.model.hasInvestigationEnded;
    }

    @computed
    public get isFormDisabled(): boolean {
        return this.isViewOnly;
    }

    @computed
    public get canShowNotInSystemField(): boolean {
        return this.model.casualtyUserId === this.model.notInSystemOption.id;
    }

    @computed
    public get canShowCasualtyDetails(): boolean {
        return !this.canShowNotInSystemField && this.model.casualtyUserId != "";
    }

    @computed
    public get canSaveForm(): boolean {
        if (!this.model.hasId) {
            return true;
        } else if (this.model.hasId && this.model.hasInvestigationStarted && !this.model.hasInvestigationEnded) {
            return true;
        } else {
            return false;
        }
    }

    @computed
    public get canStartInvestigation(): boolean {
        return this.model.hasId && !this.model.hasInvestigationStarted;
    }

    @computed
    public get getTodayDateFormatted(): string {
        return this.model.createdDate ? moment(this.model.createdDate).format("DD/MM/YYYY").toString() : moment().format("DD/MM/YYYY").toString();
    }

    @computed
    public get getInvestigationDateFormatted(): string {
        return this.model.investigationDate ? moment(this.model.investigationDate).format("DD/MM/YYYY").toString() : moment().format("DD/MM/YYYY").toString();
    }

    @computed
    public get getProjectReferenceFormatted(): string {
        if (!this.incidentProjectDetails) {
            return "";
        }

        return this.incidentProjectDetails.projectReference;
    }

    @computed
    public get getProjectName(): string {
        if (!this.incidentProjectDetails) {
            return "";
        }

        return this.incidentProjectDetails.projectName;
    }

    @computed
    public get getCasualtyUserCompany(): string {
        if (!this.casualtyUserDetails) {
            return "";
        }

        return this.casualtyUserDetails.company;
    }

    @computed
    public get getCasualtyUserEmailAdress(): string {
        if (!this.casualtyUserDetails) {
            return "";
        }

        return this.casualtyUserDetails.emailAddress;
    }

    @computed
    public get getCasualtyUserPhoneNumber(): string {
        if (!this.casualtyUserDetails) {
            return "";
        }

        return this.casualtyUserDetails.phoneNumber;
    }

    @computed
    public get getCasualtySignatureDateFormatted(): string {
        return this.model.casualtySignatureDate
            ? `${moment(this.model.casualtySignatureDate).format("DD/MM/YYYY").toString()} @ ${moment(this.model.casualtySignatureDate).format("HH:mm").toString()}`
            : `${moment().format("DD/MM/YYYY").toString()} @ ${moment().format("HH:mm").toString()}`;
    }

    @computed
    public get getFirstAiderSignatureDateFormatted(): string {
        return this.model.firstAiderSignatureDate
            ? `${moment(this.model.firstAiderSignatureDate).format("DD/MM/YYYY").toString()} @ ${moment(this.model.firstAiderSignatureDate).format("HH:mm").toString()}`
            : `${moment().format("DD/MM/YYYY").toString()} @ ${moment().format("HH:mm").toString()}`;
    }

    @observable
    public investigatorUser: { id: string; displayName: string } | null = null;

    @action
    public handleSetInvestigatorUser = (item: { id: string; displayName: string }) => {
        this.investigatorUser = item;
        this.model.investigatorUserId = item.id;
    };

    @computed
    public get getRootCauses(): GenericFreeTextListItemModel[] {
        return this.model.incidentFormAccidentRootCauses.filter((d) => !d.isDeleted);
    }

    @computed
    public get getSelectedInjuryAreas(): AccidentInjuryAreaItemModel[] {
        return this.model.incidentFormAccidentInjuryAreas.filter((i) => !i.isDeleted);
    }

    @computed
    public get getSelectedInjuryTypes(): AccidentInjuryTypeItemModel[] {
        return this.model.incidentFormAccidentInjuryTypes.filter((i) => !i.isDeleted);
    }

    @computed
    public get getSelectedWitnesses(): AccidentWitnessItemModel[] {
        return this.model.incidentFormAccidentWitnesses.filter((i) => !i.isDeleted);
    }

    @action
    public addRootCause = (rootCauseName: string): void => {
        let rootCauseToAdd: GenericFreeTextListItemDTO = {
            id: null,
            name: rootCauseName,
            isDeleted: false,
            createdByUserId: null,
            createdDate: null,
        };

        let model: GenericFreeTextListItemModel = new GenericFreeTextListItemModel();
        model.fromDto(rootCauseToAdd);
        this.model.incidentFormAccidentRootCauses.push(model);
    };

    @action
    public addInjuryArea = (): void => {
        let selectedAreaId = this.injuryArea ? this.injuryArea.id : null;
        let selectedAreaName = this.injuryArea ? this.injuryArea.displayName : null;

        const isNotInSystem: boolean = selectedAreaName === "Other";

        let injuryAreaToAdd: AccidentInjuryAreaItemDTO = {
            id: null,
            notInSystem: "",
            injuryAreaId: isNotInSystem ? null : selectedAreaId,
            isDeleted: false,
            createdByUserId: null,
            createdDate: null,
            displayName: isNotInSystem ? "" : selectedAreaName,
            incidentAccidentId: this.model.hasId ? this.model.id : null,
            typeId: selectedAreaId,
            isNotInSystem: isNotInSystem,
        };

        let model: AccidentInjuryAreaItemModel = new AccidentInjuryAreaItemModel();
        model.fromDto(injuryAreaToAdd);
        this.model.incidentFormAccidentInjuryAreas.push(model);

        this.injuryArea = null;
    };

    @action
    public addInjuryType = (): void => {
        let selectedTypeId = this.injuryType ? this.injuryType.id : null;
        let selectedTypeName = this.injuryType ? this.injuryType.displayName : null;

        const isNotInSystem: boolean = selectedTypeName === "Other";

        let injuryTypeToAdd: AccidentInjuryTypeItemDTO = {
            id: null,
            notInSystem: "",
            injuryTypeId: isNotInSystem ? null : selectedTypeId,
            isDeleted: false,
            createdByUserId: null,
            createdDate: null,
            displayName: isNotInSystem ? "" : selectedTypeName,
            incidentAccidentId: this.model.hasId ? this.model.id : null,
            typeId: selectedTypeId,
            isNotInSystem: isNotInSystem,
        };

        let model: AccidentInjuryTypeItemModel = new AccidentInjuryTypeItemModel();
        model.fromDto(injuryTypeToAdd);
        this.model.incidentFormAccidentInjuryTypes.push(model);

        this.injuryType = null;
    };

    @action
    public addWitness = (): void => {
        let selectedUserId = this.witnessUser ? this.witnessUser.id : null;
        let selectedUserName = this.witnessUser ? this.witnessUser.displayName : null;

        const isNotInSystem: boolean = selectedUserId === this.model.notInSystemOption.id;

        let witnessToAdd: AccidentWitnessItemDTO = {
            id: null,
            notInSystem: "",
            witnessUserId: isNotInSystem ? null : selectedUserId,
            isDeleted: false,
            createdByUserId: null,
            createdDate: null,
            displayName: isNotInSystem ? "" : selectedUserName,
            incidentAccidentId: this.model.hasId ? this.model.id : null,
            typeId: selectedUserId,
            isNotInSystem: isNotInSystem,
        };

        let model: AccidentWitnessItemModel = new AccidentWitnessItemModel();
        model.fromDto(witnessToAdd);
        this.model.incidentFormAccidentWitnesses.push(model);

        this.witnessUser = null;
    };

    @action
    public deleteInjuryArea = (id: string | null = null): void => {
        let model = this.model.incidentFormAccidentInjuryAreas.find((i) => !i.isDeleted && i.injuryAreaId === id);
        if (model) {
            model.handleDelete(true);
        }
    };

    @action
    public deleteInjuryType = (id: string | null = null): void => {
        let model = this.model.incidentFormAccidentInjuryTypes.find((i) => !i.isDeleted && i.injuryTypeId === id);
        if (model) {
            model.handleDelete(true);
        }
    };

    @action
    public deleteWitness = (id: string | null = null): void => {
        let model = this.model.incidentFormAccidentWitnesses.find((i) => !i.isDeleted && i.witnessUserId === id);
        if (model) {
            model.handleDelete(true);
        }
    };

    @computed
    public get getDocuments(): AccidentPhotoListItemModel[] {
        return this.model.incidentFormAccidentPhotos.filter((d) => d.isDeleted === false);
    }

    @computed
    public get getInjuryDetailsDocuments(): AccidentPhotoListItemModel[] {
        return this.getDocuments.filter((d) => d.documentType === AccidentDocumentType.InjuryDetails);
    }

    @computed
    public get getTrainingDocuments(): AccidentPhotoListItemModel[] {
        return this.getDocuments.filter((d) => d.documentType === AccidentDocumentType.Training);
    }

    @computed
    public get getEquipmentMaintenanceDocuments(): AccidentPhotoListItemModel[] {
        return this.getDocuments.filter((d) => d.documentType === AccidentDocumentType.EquipmentMaintenance);
    }

    @computed
    public get getRAAndSignedSheetDocuments(): AccidentPhotoListItemModel[] {
        return this.getDocuments.filter((d) => d.documentType === AccidentDocumentType.RAAndSignedSheet);
    }

    @computed
    public get getMSAndSignedSheetDocuments(): AccidentPhotoListItemModel[] {
        return this.getDocuments.filter((d) => d.documentType === AccidentDocumentType.MSAndSignedSheet);
    }

    public getCasualtyUserDetails = async (id: string): Promise<void> => {
        this.setIsLoading(true);
        return await this.server
            .query<CasualtyUserDetailsDTO>(
                () => this.Get(`${AppUrls.Server.Projects.Construction.Incident.Accident.Load.GetCasualtyUserDetails}\\${id}`),
                (result) => {
                    runInAction(() => {
                        this.casualtyUserDetails = result;
                    });
                },
            )
            .finally(() => {
                this.setIsLoading(false);
                this.setHasLoaded(true);
            });
    };

    public loadRelated = async (): Promise<void> => {
        this.setIsLoading(true);
        return await this.server
            .query<IncidentFormAccidentRelatedResponseDTO>(
                () => this.Get(`${AppUrls.Server.Projects.Construction.Incident.Accident.Load.Related}\\${this.model.incidentId}`),
                (result) => {
                    runInAction(() => {
                        this.incident = result.incident;
                        this.incidentProjectDetails = result.incidentProjectDetails;
                        result.incidentUsers.unshift(this.model.notInSystemOption);
                        this.incidentUsers.replace(result.incidentUsers);
                        this.injuryAreas.replace(result.injuryAreas);
                        this.statusOfCasualtyTypes.replace(result.statusOfCasualtyTypes);
                    });
                },
            )
            .finally(() => {
                this.setIsLoading(false);
                this.setHasLoaded(true);
            });
    };

    public loadWithRelated = async (): Promise<void> => {
        this.setIsLoading(true);
        return await this.server
            .query<IncidentFormAccidentAndRelatedResponseDTO>(
                () => this.Get(`${AppUrls.Server.Projects.Construction.Incident.Accident.Load.WithRelatedById}\\${this.model.id}`),
                (result) => {
                    runInAction(() => {
                        this.handleSetLoadWithRelated(result);
                    });
                },
            )
            .finally(() => {
                this.setIsLoading(false);
                this.setHasLoaded(true);
            });
    };
    @action
    public setHasLoaded = (val: boolean) => {
        this.hasLoaded = val;
    };
    @action
    public handleCancel(): void {
        const projectId: string = this.incident!.projectId;
        const incidentId: string = this.incident!.id!;
        //this.reset();
        this.history.push(AppUrls.Client.Project.Incident.View.replace(":projectid", projectId).replace(":incidentid", incidentId));
    }

    public upsert = async (continueToInvestigation: boolean): Promise<void> => {
        this.setIsLoading(true);

        const model: IncidentFormAccidentDTO = this.model.toDto();
        const photos: AccidentPhotoListItemDTO[] = this.model.incidentFormAccidentPhotos.map((p) => p.toDto());
        const rootCauses: GenericFreeTextListItemDTO[] = this.model.incidentFormAccidentRootCauses.map((r) => r.toDto());
        const injuryAreas: AccidentInjuryAreaItemDTO[] = this.model.incidentFormAccidentInjuryAreas.map((r) => r.toDto());
        const injuryTypes: AccidentInjuryTypeItemDTO[] = this.model.incidentFormAccidentInjuryTypes.map((r) => r.toDto());
        const witnesses: AccidentWitnessItemDTO[] = this.model.incidentFormAccidentWitnesses.map((r) => r.toDto());
        const projectId: string = this.incident!.projectId;
        const incidentId: string = this.incident!.id!;

        const request: UpsertIncidentFormAccidentAndRelatedRequestDTO = {
            incident: this.incident!,
            incidentFormAccident: model,
            incidentFormAccidentDocuments: photos,
            incidentFormAccidentRootCauses: rootCauses,
            incidentFormAccidentInjuryAreas: injuryAreas,
            incidentFormAccidentInjuryTypes: injuryTypes,
            incidentFormAccidentWitnesses: witnesses,
        };

        if (continueToInvestigation === true) {
            model.hasInvestigationStarted = true;
        }

        if (model.hasInvestigationStarted && model.investigationDate !== null) {
            model.hasInvestigationEnded = true;
        }

        let casualtyUserId: string | null = null;

        if (this.model.casualtyUserId === this.model.notInSystemOption.id) {
            casualtyUserId = null;
        } else {
            casualtyUserId = this.model.casualtyUserId;
        }

        request.incidentFormAccident.casualtyUserId = casualtyUserId;

        return await this.server
            .command<IncidentFormAccidentAndRelatedResponseDTO>(
                () => this.Post(AppUrls.Server.Projects.Construction.Incident.Accident.Upsert, request),
                (result: IncidentFormAccidentAndRelatedResponseDTO) => {
                    runInAction(() => {
                        if (!continueToInvestigation) {
                            this.reset();
                            this.history.push(AppUrls.Client.Project.Incident.View.replace(":projectid", projectId).replace(":incidentid", incidentId));
                        } else {
                            this.handleSetLoadWithRelated(result);
                        }
                    });
                },
                this.isMyModelValid,
                "There was an error trying to send the accident form",
            )
            .finally(() => this.setIsLoading(false));
    };

    private handleSetLoadWithRelated = (result: IncidentFormAccidentAndRelatedResponseDTO) => {
        this.resetListValidationMessages();
        result.incidentUsers.unshift(this.model.notInSystemOption);
        this.model.fromDto(result);
        this.incident = result.incident;
        this.incidentProjectDetails = result.incidentProjectDetails;
        this.casualtyUserDetails = result.casualtyUserDetails;
        this.incidentUsers.replace(result.incidentUsers);
        this.injuryAreas.replace(result.injuryAreas);
        this.injuryTypes.replace(result.injuryTypes);
        this.statusOfCasualtyTypes.replace(result.statusOfCasualtyTypes);
        this.accidentCauses.replace(result.causeOfAccidentTypes);
        this.hazardSources.replace(result.sourcesOfHazardTypes);
        this.medicalPracticeTypes.replace(result.medicalPracticeTypes);
        this.drugTestResultTypes.replace(result.drugTestResultTypes);
        if (this.model.investigatorUserId && this.incidentUsers.length > 0) {
            const userToAssign: IncidentUserDTO | undefined = this.incidentUsers.find((i) => i.id === this.model.investigatorUserId);
            if (userToAssign) {
                this.handleSetInvestigatorUser(userToAssign);
            }
        }

        if (this.model.casualtyUserId && this.incidentUsers.length > 0 && this.model.hasId) {
            const userToAssign: IncidentUserDTO | undefined = this.incidentUsers.find((i) => i.id === this.model.casualtyUserId);
            if (userToAssign) {
                this.handleSetCasualtyUser(userToAssign);
            } else {
                this.handleSetCasualtyUser(this.model.notInSystemOption);
            }
        } else if (this.model.casualtyUserId === null && this.incidentUsers.length > 0 && this.model.hasId) {
            this.handleSetCasualtyUser(this.model.notInSystemOption);
        }

        if (this.model.causeOfAccidentTypeId && this.accidentCauses.length > 0) {
            const causeToAssign: GenericListItemDTO | undefined = this.accidentCauses.find((i) => i.id === this.model.causeOfAccidentTypeId);
            if (causeToAssign) {
                this.handleSetCauseOfAccidentType(causeToAssign);
            }
        }

        if (this.model.sourceOfHazardTypeId && this.hazardSources.length > 0) {
            const hazardToAssign: GenericListItemDTO | undefined = this.hazardSources.find((i) => i.id === this.model.sourceOfHazardTypeId);
            if (hazardToAssign) {
                this.handleSetSourceOfHazardType(hazardToAssign);
            }
        }

        if (this.model.statusOfCasualtyId && this.statusOfCasualtyTypes.length > 0) {
            const statusToAssign: GenericListItemDTO | undefined = this.statusOfCasualtyTypes.find((i) => i.id === this.model.statusOfCasualtyId);
            if (statusToAssign) {
                this.handleSetStatusOfCasualty(statusToAssign);
            }
        }

        if (this.model.whichMedicalPracticeId && this.medicalPracticeTypes.length > 0) {
            const practiceToAssign: GenericListItemDTO | undefined = this.medicalPracticeTypes.find((i) => i.id === this.model.whichMedicalPracticeId);
            if (practiceToAssign) {
                this.handleSetMedicalPracticeType(practiceToAssign);
            }
        }

        if (this.model.alcoholOrDrugTestingRequiredResultId && this.drugTestResultTypes.length > 0) {
            const resultToAssign: GenericListItemDTO | undefined = this.drugTestResultTypes.find((i) => i.id === this.model.alcoholOrDrugTestingRequiredResultId);
            if (resultToAssign) {
                this.handleSetDrugTestResultType(resultToAssign);
            }
        }
    };

    /**
     * Handle a file being selected and process the data for upload.
     * @param event
     */
    @action
    public fileChange = async (event: React.ChangeEvent<HTMLInputElement>, documentType: AccidentDocumentType): Promise<void> => {
        if (event.target.files !== null && event.target.value !== null && event.target.files.length > 0) {
            let data: any = {
                fileName: event.target.files[0].name,
                formFile: event.target.files[0],
            };
            event.target.value = "";
            const apiResult = await this.fileUpload(data);
            if (apiResult && apiResult.wasSuccessful) {
                let fileToDisplay: AccidentPhotoListItemDTO = {
                    id: null,
                    photoURL: apiResult.payload,
                    fileName: data.fileName,
                    isDeleted: false,
                    createdByUserId: null,
                    createdDate: null,
                    documentType: documentType,
                };

                let model: AccidentPhotoListItemModel = new AccidentPhotoListItemModel();
                model.fromDto(fileToDisplay);
                runInAction(() => this.model.incidentFormAccidentPhotos.push(model));
            }
        }
    };

    /**
     * Upload a file to azure.
     * @param data The data of the file to be uploaded.
     * @returns apiResult.
     */
    public fileUpload = async (data: any): Promise<ApiResult<any>> => {
        const formData = new FormData();
        formData.append("formFile", data.formFile);
        formData.append("fileName", data.fileName);
        const apiResult = await this.Post<any>(AppUrls.Server.File.UploadFile, formData);
        if (apiResult) {
            if (!apiResult.wasSuccessful) {
                console.log(apiResult.errors);
                runInAction(() => {
                    this.setSnackMessage("Error uploading file please try again.");
                    this.setSnackType(this.SNACKERROR);
                    this.setSnackbarState(true);
                });
            }
        }
        return apiResult;
    };

    /**
     * Download a file that exists in azure.
     * @param fileUrl The URL of the file to be downloaded.
     * @param fileName The name of the file to be downloaded.
     */
    public DownloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
        try {
            const apiResult = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, fileUrl, undefined, { responseType: "blob" });
            const response = apiResult as any;
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            URL.revokeObjectURL(url);
            link.remove();
        } catch (exception) {
            console.error(exception);
            this.setIsErrored(true);
        }
    };

    /**
     * Custom model validation function. Validates child models and its children
     * @returns True if model is valid, false if not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        this.resetListValidationMessages();
        let isValid = true;

        // Handle witnesses.
        if (this.model.incidentFormAccidentWitnesses.length == 0) {
            isValid = false;

            this.setWitnessesValidationMessage("Please provide a witness");
        }

        for (let i = 0; i < this.model.incidentFormAccidentWitnesses.length; i++) {
            let witness = this.model.incidentFormAccidentWitnesses[i];

            if (witness.isNotInSystem && (witness.notInSystem === "" || witness.notInSystem === null || witness.notInSystem === undefined) && !witness.isDeleted) {
                isValid = false;

                this.setWitnessesValidationMessage("Please provide a not in system name");
            }
        }

        // Handle injury areas.
        if (this.model.incidentFormAccidentInjuryAreas.length == 0) {
            isValid = false;

            this.setInjuryAreasValidationMessage("Please provide an injury area");
        }

        for (let i = 0; i < this.model.incidentFormAccidentInjuryAreas.length; i++) {
            let injuryArea = this.model.incidentFormAccidentInjuryAreas[i];

            if (injuryArea.isNotInSystem && (injuryArea.notInSystem === "" || injuryArea.notInSystem === null || injuryArea.notInSystem === undefined) && !injuryArea.isDeleted) {
                isValid = false;

                this.setInjuryAreasValidationMessage("Please provide an other name");
            }
        }

        // Handle injury types.
        if (this.model.incidentFormAccidentInjuryTypes.length == 0 && this.model.hasInvestigationStarted) {
            isValid = false;

            this.setInjuryTypesValidationMessage("Please provide an injury type");
        }

        if (this.model.hasInvestigationStarted) {
            for (let i = 0; i < this.model.incidentFormAccidentInjuryTypes.length; i++) {
                let injuryType = this.model.incidentFormAccidentInjuryTypes[i];

                if (
                    injuryType.isNotInSystem &&
                    (injuryType.notInSystem === "" || injuryType.notInSystem === null || injuryType.notInSystem === undefined) &&
                    !injuryType.isDeleted
                ) {
                    isValid = false;

                    this.setInjuryTypesValidationMessage("Please provide an other name");
                }
            }
        }

        // Handle root causes.
        if (this.model.incidentFormAccidentRootCauses.length == 0 && this.model.hasInvestigationStarted) {
            isValid = false;

            this.setRootCausesValidationMessage("Please provide a root cause");
        }

        if ((await this.isModelValid()) === false) {
            isValid = false;
        }

        return isValid;
    };

    @action
    public reset = () => {
        this.model.reset();
        this.server.reset();
        this.incidentUsers.length = 0;
        this.incidentProjectDetails = null;
        this.casualtyUserDetails = null;
        this.incident = null;
        this.resetListValidationMessages();
    };

    // #region Snackbar

    @observable
    public snackbarState = false;

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @observable
    public snackMessage = "";

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @observable
    public snackType = "";

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    @observable
    public SNACKSUCCESS = "success";

    @observable
    public SNACKERROR = "error";
    // #endregion

    // #region First form validation

    @computed
    private get validateCasualtyUserId() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.casualtyUserId) || this.model.casualtyUserId === "" ? "Please provide a casualty user" : "";

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateCasualtyNotInSystem() {
        let errorMessage = "";

        if (this.canShowNotInSystemField) {
            errorMessage = isNullOrUndefined(this.model.casualtyNotInSystem) || this.model.casualtyNotInSystem === "" ? "Please provide a casualty name" : "";
        }

        if (errorMessage === "" && this.model.casualtyNotInSystem) {
            errorMessage = this.model.casualtyNotInSystem.length > 255 ? "Can be no longer than 255 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateCasualtyDateOfBirth() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.casualtyDateOfBirth) ? "Please provide a date of birth" : "";

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAddressLine1() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.addressLine1) || this.model.addressLine1 === "" ? "Please provide an address line 1" : "";

        if (errorMessage === "" && this.model.addressLine1) {
            errorMessage = this.model.addressLine1.length > 255 ? "Can be no longer than 255 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAddressLine2() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.addressLine2) || this.model.addressLine2 === "" ? "Please provide an address line 2" : "";

        if (errorMessage === "" && this.model.addressLine2) {
            errorMessage = this.model.addressLine2.length > 255 ? "Can be no longer than 255 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAddressLine3() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.addressLine3) || this.model.addressLine3 === "" ? "Please provide an address line 3" : "";

        if (errorMessage === "" && this.model.addressLine3) {
            errorMessage = this.model.addressLine3.length > 255 ? "Can be no longer than 255 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateCityTown() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.cityTown) || this.model.cityTown === "" ? "Please provide a city or town" : "";

        if (errorMessage === "" && this.model.cityTown) {
            errorMessage = this.model.cityTown.length > 255 ? "Can be no longer than 255 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validatePostcode() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.postcode) || this.model.postcode === "" ? "Please provide a postcode" : "";

        if (errorMessage === "" && this.model.postcode) {
            errorMessage = this.model.postcode.length > 255 ? "Can be no longer than 255 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateStatusOfCasualtyId() {
        let errorMessage = "";

        if (this.canShowNotInSystemField) {
            errorMessage = isNullOrUndefined(this.model.statusOfCasualtyId) || this.model.statusOfCasualtyId === "" ? "Please provide a casualty status" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateCasualtyPhoneNumber() {
        let errorMessage = "";

        if (this.canShowNotInSystemField) {
            errorMessage = isNullOrUndefined(this.model.casualtyPhoneNumber) || this.model.casualtyPhoneNumber === "" ? "Please provide a phone number" : "";
        }

        if (errorMessage === "" && this.model.casualtyPhoneNumber) {
            errorMessage = this.model.casualtyPhoneNumber.length > 255 ? "Can be no longer than 16 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateCasualtyEmailAddress() {
        let errorMessage = "";

        if (this.canShowNotInSystemField) {
            errorMessage = isNullOrUndefined(this.model.casualtyEmailAddress) || this.model.casualtyEmailAddress === "" ? "Please provide an email address" : "";
        }

        if (errorMessage === "" && this.model.casualtyEmailAddress) {
            errorMessage = this.model.casualtyEmailAddress.length > 255 ? "Can be no longer than 256 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAccidentDate() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.accidentDate) ? "Please provide an accident date and time" : "";

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateLocation() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.location) || this.model.location.trim() === "" ? "Please provide a location" : "";

        if (errorMessage === "" && this.model.location) {
            errorMessage = this.model.location.length > 255 ? "Can be no longer than 256 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWhatHappened() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.whatHappened) || this.model.whatHappened.trim() === "" ? "Please provide what happened" : "";

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDescriptionOfInjury() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.descriptionOfInjury) || this.model.descriptionOfInjury.trim() === "" ? "Please provide a description of the injury" : "";

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDescriptionOfTreatment() {
        let errorMessage = "";

        errorMessage =
            isNullOrUndefined(this.model.descriptionOfTreatment) || this.model.descriptionOfTreatment.trim() === "" ? "Please provide a description of the treatment" : "";

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateCasualtySignatureBase64() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.casualtySignatureBase64) || this.model.casualtySignatureBase64 === "" ? "Please provide a casualty signature" : "";

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateFirstAiderSignature() {
        let errorMessage = "";

        errorMessage = isNullOrUndefined(this.model.firstAiderSignature) || this.model.firstAiderSignature === "" ? "Please provide a first aider signature" : "";

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    // #endregion

    // #region Second form validation

    @computed
    private get validateInvestigatorUserId() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.investigatorUserId) || this.model.investigatorUserId === "" ? "Please select an investigator" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDescriptionOfInjuryFurtherInfo() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage =
                isNullOrUndefined(this.model.descriptionOfInjuryFurtherInfo) || this.model.descriptionOfInjuryFurtherInfo?.trim() === ""
                    ? "Please provide a description of injury"
                    : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateFatality() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.fatality) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateUnconscious() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.unconscious) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateResuscitation() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.resuscitation) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAdvisedToSeeDoctor() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.advisedToSeeDoctor) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWhichMedicalPracticeId() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.model.advisedToSeeDoctor === true) {
            errorMessage = isNullOrUndefined(this.model.whichMedicalPracticeId) ? "Please select the medical practice" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAlcoholOrDrugTestingRequired() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.alcoholOrDrugTestingRequired) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAlcoholOrDrugTestingRequiredResultId() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.model.alcoholOrDrugTestingRequired === true) {
            errorMessage = isNullOrUndefined(this.model.alcoholOrDrugTestingRequiredResultId) ? "Please select the result" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAdditionalInjuriesOrWorsening() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage =
                isNullOrUndefined(this.model.additionalInjuriesOrWorsening) || this.model.additionalInjuriesOrWorsening?.trim() === ""
                    ? "Please provide any additional injuries found or worsening of original injury:"
                    : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateCauseOfAccidentTypeId() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.causeOfAccidentTypeId) ? "Please select the cause of accident" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateOtherCauseOfAccident() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.isOtherCauseOfAccident) {
            errorMessage = isNullOrUndefined(this.model.otherCauseOfAccident) ? "Please provide another cause of accident" : "";
        }

        if (errorMessage === "" && this.model.otherCauseOfAccident) {
            errorMessage = this.model.otherCauseOfAccident.length > 255 ? "Can be no longer than 256 characters" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateFallHeight() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.isFallHeightCauseOfAccident) {
            errorMessage = isNullOrUndefined(this.model.fallHeight) ? "Please provide the fall height" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateSourceOfHazardTypeId() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.sourceOfHazardTypeId) ? "Please select the source of hazard" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateOtherSourceOfHazard() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.isOtherSourceOfHazard) {
            errorMessage = isNullOrUndefined(this.model.otherSourceOfHazard) ? "Please provide another source of hazard" : "";

            if (errorMessage === "" && this.model.otherSourceOfHazard) {
                errorMessage = this.model.otherSourceOfHazard.length > 255 ? "Can be no longer than 255 characters" : "";
            }
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWhichPartOfWorkProcess() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage =
                isNullOrUndefined(this.model.whichPartOfWorkProcess) || this.model.whichPartOfWorkProcess?.trim() === "" ? "Please provide a description the work process" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDescriptionOfEnvironment() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage =
                isNullOrUndefined(this.model.descriptionOfEnvironment) || this.model.descriptionOfEnvironment?.trim() === "" ? "Please provide a description the environment" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDescriptionOfEquipment() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage =
                isNullOrUndefined(this.model.descriptionOfEquipment) || this.model.descriptionOfEquipment?.trim() === "" ? "Please provide a description the equipment" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validatePPERequired() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.ppeRequired) || this.model.ppeRequired?.trim() === "" ? "Please provide a description the PPE required" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validatePPEWorn() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.ppeWorn) || this.model.ppeWorn?.trim() === "" ? "Please provide a description the PPE worn" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWasThereARiskAssessment() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.wasThereARiskAssessment) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWasThereAMethodStatement() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.wasThereAMethodStatement) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWasTheAccidentRIDDORReportable() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.wasTheAccidentRIDDORReportable) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDateRIDDORWasSent() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.model.wasTheAccidentRIDDORReportable === true) {
            errorMessage = isNullOrUndefined(this.model.dateRIDDORWasSent) ? "Please provide a date and time" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWasAnyTimeLost() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted) {
            errorMessage = isNullOrUndefined(this.model.wasAnyTimeLost) ? "Please provide an answer" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDateWorkFinished() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.model.wasAnyTimeLost === true) {
            errorMessage = isNullOrUndefined(this.model.dateWorkFinished) ? "Please provide a date and time" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDateWorkRestarted() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.model.wasAnyTimeLost === true) {
            errorMessage = isNullOrUndefined(this.model.dateWorkRestarted) ? "Please provide a date and time" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateTotalTimeLost() {
        let errorMessage = "";

        if (this.model.hasInvestigationStarted && this.model.wasAnyTimeLost === true) {
            errorMessage = isNullOrUndefined(this.model.totalTimeLost) ? "Please provide the total time lost" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDefectiveWorkplace() {
        const errorMessage = this.model.validateDefectiveWorkplace;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateLighting() {
        const errorMessage = this.model.validateLighting;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDesignOrLayout() {
        const errorMessage = this.model.validateDesignOrLayout;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateNoiseOrDistraction() {
        const errorMessage = this.model.validateNoiseOrDistraction;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateHousekeeping() {
        const errorMessage = this.model.validateHousekeeping;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWeather() {
        const errorMessage = this.model.validateWeather;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateLackOfRoom() {
        const errorMessage = this.model.validateLackOfRoom;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateAccessOrEgress() {
        const errorMessage = this.model.validateAccessOrEgress;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateFailureToFollowRules() {
        const errorMessage = this.model.validateFailureToFollowRules;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateLackOfExperience() {
        const errorMessage = this.model.validateLackOfExperience;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateFailureToAdhereToRiskAssessment() {
        const errorMessage = this.model.validateFailureToAdhereToRiskAssessment;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateFatigue() {
        const errorMessage = this.model.validateFatigue;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateInstructionsMisunderstood() {
        const errorMessage = this.model.validateInstructionsMisunderstood;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateUnsafeAttitude() {
        const errorMessage = this.model.validateUnsafeAttitude;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateHorseplay() {
        const errorMessage = this.model.validateHorseplay;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWorkingWithoutAuthorisation() {
        const errorMessage = this.model.validateWorkingWithoutAuthorisation;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateErrorOfJudgement() {
        const errorMessage = this.model.validateErrorOfJudgement;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateUndueHaste() {
        const errorMessage = this.model.validateUndueHaste;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateLapseOfConcentration() {
        const errorMessage = this.model.validateLapseOfConcentration;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDesign() {
        const errorMessage = this.model.validateDesign;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validatePoorlyMaintainedOrDefective() {
        const errorMessage = this.model.validatePoorlyMaintainedOrDefective;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateNotUsed() {
        const errorMessage = this.model.validateNotUsed;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateWrongTypeUsed() {
        const errorMessage = this.model.validateWrongTypeUsed;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateNotProvidedOrUnavailable() {
        const errorMessage = this.model.validateNotProvidedOrUnavailable;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateNonCommunicationOfRisk() {
        const errorMessage = this.model.validateNonCommunicationOfRisk;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateSupervision() {
        const errorMessage = this.model.validateSupervision;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateTraining() {
        const errorMessage = this.model.validateTraining;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateSystemFailure() {
        const errorMessage = this.model.validateSystemFailure;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateConstructionOrDesign() {
        const errorMessage = this.model.validateConstructionOrDesign;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateSafetyDevice() {
        const errorMessage = this.model.validateSafetyDevice;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateMechanicalFailure() {
        const errorMessage = this.model.validateMechanicalFailure;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateInstallation() {
        const errorMessage = this.model.validateInstallation;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateOperationOrUse() {
        const errorMessage = this.model.validateOperationOrUse;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateThirdParty() {
        const errorMessage = this.model.validateThirdParty;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateUnderInvestigation() {
        const errorMessage = this.model.validateUnderInvestigation;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateActionsToRecover() {
        const errorMessage = this.model.validateActionsToRecover;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateActionsToPrevent() {
        const errorMessage = this.model.validateActionsToPrevent;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    // #endregion

    public async isFieldValid(fieldName: keyof FieldType<AccidentFormModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        if (this.server.IsSubmitted) {
            switch (fieldName) {
                // Start of first form validation
                case "casualtyUserId": {
                    const result = this.validateCasualtyUserId;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "casualtyNotInSystem": {
                    const result = this.validateCasualtyNotInSystem;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "casualtyDateOfBirth": {
                    const result = this.validateCasualtyDateOfBirth;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "addressLine1": {
                    const result = this.validateAddressLine1;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "addressLine2": {
                    const result = this.validateAddressLine2;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "addressLine3": {
                    const result = this.validateAddressLine3;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "cityTown": {
                    const result = this.validateCityTown;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "postcode": {
                    const result = this.validatePostcode;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "statusOfCasualtyId": {
                    const result = this.validateStatusOfCasualtyId;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "casualtyPhoneNumber": {
                    const result = this.validateCasualtyPhoneNumber;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "casualtyEmailAddress": {
                    const result = this.validateCasualtyEmailAddress;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "accidentDate": {
                    const result = this.validateAccidentDate;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "location": {
                    const result = this.validateLocation;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "whatHappened": {
                    const result = this.validateWhatHappened;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "descriptionOfInjury": {
                    const result = this.validateDescriptionOfInjury;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "descriptionOfTreatment": {
                    const result = this.validateDescriptionOfTreatment;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "casualtySignatureBase64": {
                    const result = this.validateCasualtySignatureBase64;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "firstAiderSignature": {
                    const result = this.validateFirstAiderSignature;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }

                // End of first form validation

                // Start of second form validation
                case "investigatorUserId": {
                    const result = this.validateInvestigatorUserId;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "descriptionOfInjuryFurtherInfo": {
                    const result = this.validateDescriptionOfInjuryFurtherInfo;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "fatality": {
                    const result = this.validateFatality;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "unconscious": {
                    const result = this.validateUnconscious;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "resuscitation": {
                    const result = this.validateResuscitation;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "advisedToSeeDoctor": {
                    const result = this.validateAdvisedToSeeDoctor;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "whichMedicalPracticeId": {
                    const result = this.validateWhichMedicalPracticeId;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "alcoholOrDrugTestingRequired": {
                    const result = this.validateAlcoholOrDrugTestingRequired;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "alcoholOrDrugTestingRequiredResultId": {
                    const result = this.validateAlcoholOrDrugTestingRequiredResultId;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "additionalInjuriesOrWorsening": {
                    const result = this.validateAdditionalInjuriesOrWorsening;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "causeOfAccidentTypeId": {
                    const result = this.validateCauseOfAccidentTypeId;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "otherCauseOfAccident": {
                    const result = this.validateOtherCauseOfAccident;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "fallHeight": {
                    const result = this.validateFallHeight;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "sourceOfHazardTypeId": {
                    const result = this.validateSourceOfHazardTypeId;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "otherSourceOfHazard": {
                    const result = this.validateOtherSourceOfHazard;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "whichPartOfWorkProcess": {
                    const result = this.validateWhichPartOfWorkProcess;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "descriptionOfEnvironment": {
                    const result = this.validateDescriptionOfEnvironment;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "descriptionOfEquipment": {
                    const result = this.validateDescriptionOfEquipment;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "ppeRequired": {
                    const result = this.validatePPERequired;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "ppeWorn": {
                    const result = this.validatePPEWorn;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "wasThereARiskAssessment": {
                    const result = this.validateWasThereARiskAssessment;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "wasThereAMethodStatement": {
                    const result = this.validateWasThereAMethodStatement;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "wasTheAccidentRIDDORReportable": {
                    const result = this.validateWasTheAccidentRIDDORReportable;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "dateRIDDORWasSent": {
                    const result = this.validateDateRIDDORWasSent;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "wasAnyTimeLost": {
                    const result = this.validateWasAnyTimeLost;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "dateWorkFinished": {
                    const result = this.validateDateWorkFinished;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "dateWorkRestarted": {
                    const result = this.validateDateWorkRestarted;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "totalTimeLost": {
                    const result = this.validateTotalTimeLost;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "defectiveWorkplace": {
                    const result = this.validateDefectiveWorkplace;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "lighting": {
                    const result = this.validateLighting;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "designOrLayout": {
                    const result = this.validateDesignOrLayout;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "noiseOrDistraction": {
                    const result = this.validateNoiseOrDistraction;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "housekeeping": {
                    const result = this.validateHousekeeping;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "weather": {
                    const result = this.validateWeather;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "lackOfRoom": {
                    const result = this.validateLackOfRoom;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "accessOrEgress": {
                    const result = this.validateAccessOrEgress;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "failureToFollowRules": {
                    const result = this.validateFailureToFollowRules;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "lackOfExperience": {
                    const result = this.validateLackOfExperience;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "failureToAdhereToRiskAssessment": {
                    const result = this.validateFailureToAdhereToRiskAssessment;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "fatigue": {
                    const result = this.validateFatigue;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "instructionsMisunderstood": {
                    const result = this.validateInstructionsMisunderstood;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "unsafeAttitude": {
                    const result = this.validateUnsafeAttitude;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "horseplay": {
                    const result = this.validateHorseplay;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "workingWithoutAuthorisation": {
                    const result = this.validateWorkingWithoutAuthorisation;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "errorOfJudgement": {
                    const result = this.validateErrorOfJudgement;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "undueHaste": {
                    const result = this.validateUndueHaste;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "lapseOfConcentration": {
                    const result = this.validateLapseOfConcentration;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "design": {
                    const result = this.validateDesign;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "poorlyMaintainedOrDefective": {
                    const result = this.validatePoorlyMaintainedOrDefective;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "notUsed": {
                    const result = this.validateNotUsed;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "wrongTypeUsed": {
                    const result = this.validateWrongTypeUsed;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "notProvidedOrUnavailable": {
                    const result = this.validateNotProvidedOrUnavailable;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "nonCommunicationOfRisk": {
                    const result = this.validateNonCommunicationOfRisk;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "supervision": {
                    const result = this.validateSupervision;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "training": {
                    const result = this.validateTraining;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "systemFailure": {
                    const result = this.validateSystemFailure;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "constructionOrDesign": {
                    const result = this.validateConstructionOrDesign;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "safetyDevice": {
                    const result = this.validateSafetyDevice;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "mechanicalFailure": {
                    const result = this.validateMechanicalFailure;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "installation": {
                    const result = this.validateInstallation;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "operationOrUse": {
                    const result = this.validateOperationOrUse;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "thirdParty": {
                    const result = this.validateThirdParty;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "underInvestigation": {
                    const result = this.validateUnderInvestigation;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "actionsToRecover": {
                    const result = this.validateActionsToRecover;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "actionsToPrevent": {
                    const result = this.validateActionsToPrevent;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                // End of second form validation
            }
        } else {
            // Do not validate if the properties of the model have not been
            // submitted to the server.
            errorMessage = "";
            isValid = true;
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
